/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import { useNavigate } from 'react-router-dom';
import { AnalyticsEvents } from '@/analytics/events';
import { useDuplicateWorkersMutation, Worker } from '@/generated/graphql';
import { trackEvent } from '@/tracker';
import routes from '@/utils/routes';
import { ApolloError } from '@apollo/client';
import { notification } from 'antd';
import React, { useEffect } from 'react';
import useDepartment from './useDepartment';

interface UseDuplicateWorkerProps {
  workerId: string;
  isPlayground: boolean;
}
const useDuplicateWorker = ({
  workerId,
  isPlayground
}: UseDuplicateWorkerProps) => {
  const [loading, setLoading] = React.useState(false);
  const { department } = useDepartment();
  const navigate = useNavigate();

  const onMutationError = (error: ApolloError) => {
    setLoading(false);
    notification.error({
      message: 'Error',
      description: error.message
    });
  };

  const [rerun, rerunResults] = useDuplicateWorkersMutation({
    onError: onMutationError,
    onCompleted: () => setLoading(false)
  });

  const duplicateWorker = rerunResults.data?.duplicateWorkers?.[0];

  const getProcedureRunUrl = (worker: Worker) =>
    routes.procedure.getProcedureRunPageUrl(
      worker.departmentId!,
      worker.procedureId!,
      worker.id!
    );

  const initiateRun = () => {
    trackEvent(AnalyticsEvents.RETRY.ClickRunAgainButton);
    setLoading(true);
    rerun({
      variables: {
        workerIds: [workerId],
        departmentId: department.id,
        runWorker: !isPlayground
      }
    });
  };

  useEffect(() => {
    if (duplicateWorker) {
      if (isPlayground) {
        navigate(
          routes.playground.getPlaygroundPageUrl(
            department.id,
            duplicateWorker.id
          ),
          {
            replace: true
          }
        );
      } else {
        navigate(getProcedureRunUrl(duplicateWorker as Worker), {
          replace: true
        });
      }
    }
  }, [duplicateWorker]);

  return {
    loading,
    rerunResults,
    initiateRun
  };
};

export default useDuplicateWorker;
